import React, { useEffect, useState } from "react";
import {
	Card,
	Button,
	ListGroup,
	Row,
	Col,
	Placeholder,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { deleteTeam } from "../../../services/teamService";
import getCountryISO2 from "country-iso-3-to-2";
import ReactCountryFlag from "react-country-flag";
import { IoEnter } from "react-icons/io5";
import {
	TeamWithRider,
	TeamComplete,
	League,
	Leaderboard,
	LeaderboardTeam,
	User,
} from "../../../types/model";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { RootState } from "../../../store/store";
import ViewLeagueCard from "./Leagues/ViewLeagueCard";
import { FaCalendar } from "react-icons/fa";
import { getSeasons } from "../../../services/seasonService";
import { setSeasons } from "../../../store/slices/fantasySlice";
import {
	fetchSeasonsDataThunk,
	handleViewLeagueThunk,
} from "../../../store/thunks/fantasyThunk";
import {
	getLeaderboard,
	getLeaderboardsByTeamId,
} from "../../../services/leaderboardService";
import {
	getPositionInLeague,
	getTeamPointsFromLeaderboard,
} from "../../../utils/helper";
import PointsBreakdownBudgetMode from "../../League/Components/PointsBreakdownComponents/PointsBreakdownBudgetMode";
import PointsBreakdown from "../../League/Components/PointsBreakdown";

interface FantasyTeamProps {
	team: TeamComplete;
	isLoading: boolean;
	showLeagues: boolean;
	showLeagueSport: boolean;
	showLeagueSeries: boolean;
}

const FantasyTeam = ({
	team,
	isLoading,
	showLeagues = false,
	showLeagueSport = false,
	showLeagueSeries = false,
}: FantasyTeamProps) => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { isMobile, user } = useAppSelector((state: RootState) => state.user);
	const { selectedSeason, seasons } = useAppSelector(
		(state: RootState) => state.fantasy,
	);

	const [showPointsBreakdown, setShowPointsBreakdown] = useState(false);
	const [teamLeaderboards, setTeamLeaderboards] = useState<Leaderboard[]>([]);

	const seasonName = seasons.find((season) => season._id === team.season)?.name;

	useEffect(() => {
		fetchSeasons();
	}, []);

	const fetchSeasons = async () => {
		try {
			dispatch(fetchSeasonsDataThunk());
		} catch (error) {
			console.error("Error fetching seasons:", error);
		}
	};

	useEffect(() => {
		if (team.leagues.length > 0) {
			fetchTeamLeaderboards();
		}
	}, [team.leagues]);

	const fetchTeamLeaderboards = async () => {
		try {
			const res = await getLeaderboardsByTeamId(team._id);
			if (res) {
				setTeamLeaderboards(res.data);
			}
		} catch (error) {
			console.error("Error fetching team leaderboards:", error);
		}
	};

	const handleDeleteTeam = async (team: TeamWithRider) => {
		try {
			const res = await deleteTeam(team._id);

			if (res) {
				// Refresh the page
				window.location.reload();
			}
		} catch (error) {
			console.error("Error deleting team:", error);
		}
	};

	return (
		<Card
			key={team._id}
			className="fantasy-team-card mb-4 border-1 bg-secondary border-info text-light"
		>
			<Card.Header className="fantasy-team-card__header bg-secondary border-0 text-white ps-3">
				<Row className="align-items-center">
					{showLeagues ? (
						<Col
							sm={8}
							className="text-md-start text-center mb-md-0 mb-2 fs-3 text-light fw-bold"
						>
							<b className="fs-3 text-primary me-0">
								{team.series.name.toLocaleUpperCase()}{" "}
							</b>
							{(team.sport.shortName || team.sport.name).toLocaleUpperCase()}
						</Col>
					) : (
						<Col
							sm={8}
							className="text-md-start text-center mb-md-0 mb-2 fs-3 text-light fw-bold"
						>
							{" "}
							MY TEAM{" "}
						</Col>
					)}
					<Col md={4} className="text-md-end text-center">
						{selectedSeason.isActive && team.season === selectedSeason._id && (
							<Button
								variant="success"
								size={"sm"}
								className="me-3 fw-bold text-white px-3 fs-6"
								onClick={() =>
									navigate(
										`/editteam?teamId=${team._id}&sport=${team.sport._id}&series=${team.series._id}`,
									)
								}
							>
								EDIT TEAM
							</Button>
						)}
					</Col>
				</Row>
				<Row className="align-items-center">
					<Col
						sm={8}
						className="fantasy-team-card__season text-md-start text-center mb-md-0 mb-2 fs-6 text-info fw-light"
					>
						<div className="d-flex flex-row align-items-center">
							<FaCalendar className="mb-1 me-1" />
							{seasonName?.toLocaleUpperCase() || ""}
						</div>
					</Col>
				</Row>
			</Card.Header>
			{isLoading ? (
				<Placeholder as="div" animation="glow" className="my-5 mx-3">
					<Placeholder xs={8} bg="light" className="rounded-3 mb-2" />
					<Placeholder
						xs={11}
						className="rounded-3"
						bg="light"
						style={{ height: "13rem" }}
					/>
				</Placeholder>
			) : (
				<Card.Body className="fantasy-team-card__body p-4 text-light bg-secondary">
					{showLeagues && team.leagues.length > 0 && (
						<>
							<Card.Subtitle className="mb-2 ms-1 fw-bold fs-5 text-start text-info">
								LEAGUES
							</Card.Subtitle>
							<ListGroup>
								{team.leagues.map((league: League) => (
									<ViewLeagueCard
										league={league}
										currentPosition={getPositionInLeague(
											league._id ?? "",
											teamLeaderboards,
											user,
										)}
										showSport={showLeagueSport}
										showSeries={showLeagueSeries}
										onViewLeague={(leagueId) => {
											dispatch(handleViewLeagueThunk(leagueId));
											navigate(`/league/${leagueId}`);
										}}
									/>
								))}
							</ListGroup>
						</>
					)}
					{showLeagues && (
						<Card.Subtitle className="mt-4 mb-2 ms-1 fs-5 fw-bold text-start text-info">
							<div className="d-flex flex-row justify-content-between align-items-center">
								<div className="d-flex flex-row align-items-center">
									MY TEAM
									{teamLeaderboards.length > 0 &&
										team._id &&
										getTeamPointsFromLeaderboard(
											teamLeaderboards[0],
											team._id,
										) > 0 && (
											<div
												className="me-3 fw-bold text-white ms-2 fs-5"
												onClick={() => {}}
											>
												{getTeamPointsFromLeaderboard(
													teamLeaderboards[0],
													team._id,
												)}{" "}
												POINTS
											</div>
										)}
								</div>
								<Button
									variant="outline-light"
									className="fw-bold"
									size="sm"
									onClick={() => {
										setShowPointsBreakdown(true);
									}}
								>
									BREAKDOWN
								</Button>
							</div>
						</Card.Subtitle>
					)}
					<ListGroup className="fantasy-team-card__rider-list">
						{team?.riders?.map((rider) => (
							<ListGroup.Item
								key={rider?.rider?._id}
								className="fantasy-team-card__rider-row bg-secondary text-light border-1 border-dark"
							>
								<Row>
									<Col
										md={9}
										className="text-sm-start text-center my-0 my-sm-0"
									>
										{getCountryISO2(
											rider?.rider?.country?.toUpperCase().trim() ?? "",
										) ? (
											<ReactCountryFlag
												countryCode={getCountryISO2(
													rider?.rider?.country?.toUpperCase().trim() ?? "",
												)}
												svg
												style={{ width: "2.5rem" }}
											/>
										) : (
											rider?.rider?.country
										)}
										<b>{rider?.rider?.last_name}</b> {rider?.rider?.first_name}
									</Col>
									{!isMobile && (
										<Col
											md={3}
											className="fantasy-team-card__rider-status text-md-end text-center my-2 my-sm-0 text-info"
										>
											{rider?.rider?.status?.toLocaleUpperCase()}
										</Col>
									)}
								</Row>
							</ListGroup.Item>
						))}
					</ListGroup>
				</Card.Body>
			)}
			<PointsBreakdown
				leaderboardTeam={
					teamLeaderboards[0]?.teams?.find(
						(leaderboardTeam: LeaderboardTeam) =>
							leaderboardTeam.teamId === team._id,
					) ?? ({} as LeaderboardTeam)
				}
				showModal={showPointsBreakdown}
				handleClose={() => setShowPointsBreakdown(false)}
			/>
		</Card>
	);
};

export default FantasyTeam;
